import { parseISO } from 'date-fns';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { usePatientTreatmentPlanVersionsQuery } from '@/hooks/useTreatmentPlan';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import type { TreatmentPlanVersionSummary } from '@/api/treatmentPlans/treatmentPlansApi.types';

interface TreatmentPlansSectionProps {
  patientId: string;
}

export function TreatmentPlansSection({ patientId }: TreatmentPlansSectionProps) {
  const { t } = useTranslation(['patients', 'plans']);
  const navigate = useNavigate();
  const versionsQuery = usePatientTreatmentPlanVersionsQuery(patientId);
  const versions = versionsQuery.data === undefined ? [] : versionsQuery.data;

  function openVersion(version: TreatmentPlanVersionSummary) {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientTreatmentPlan, {
        patientId,
        planId: version.status === 'active' ? 'current' : version.id,
      })
    );
  }

  return (
    <RelatedRecordsSection
      title={t('patients:treatmentsTab.plansTitle')}
      isLoading={versionsQuery.isLoading}
      isError={versionsQuery.isError}
      onRetry={function retry() {
        versionsQuery.refetch();
      }}
    >
      {versions.length === 0 ? (
        <p className="text-sm text-zoomuted py-2">{t('patients:treatmentsTab.plansEmpty')}</p>
      ) : (
        <ul className="space-y-2">
          {versions.map(function renderVersion(version) {
            return (
              <li key={version.id}>
                <button
                  type="button"
                  onClick={function open() {
                    openVersion(version);
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-zooborder text-left hover:border-zooprimary transition-colors"
                >
                  <span className="text-sm font-semibold text-zooindigo">
                    {t('plans:editor.versionLabel', { version: version.version })}
                    {' · '}
                    {version.status === 'active'
                      ? t('plans:editor.statusActive')
                      : t('plans:editor.statusSuperseded')}
                  </span>
                  <span className="text-xs text-zoomuted shrink-0">
                    {formatDateToDayMonthYear(parseISO(version.createdAt))}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </RelatedRecordsSection>
  );
}
